import { ActionIcon, Group, Stack, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import { IconPlus } from '@tabler/icons-react';
import { IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../../../shared/components/UI/Button/Button';
import { TextArea } from '../../../../../../../shared/components/UI/TextArea/TextArea';
import { TextInput } from '../../../../../../../shared/components/UI/TextInput/TextInput';

import type {
  FormValues,
  FormVaultPropertyOption,
} from '../hooks/useVaultPropertyForm';

type VaultPropertyOptionInputsProps = {
  form: UseFormReturnType<FormValues>;
  isSubmitting: boolean;
  removeOption: (index: number) => void;
  addOptionInput: () => void;
  markOptionAsUpdated: (index: number) => void;
};

export function VaultPropertyOptionInputs({
  form,
  isSubmitting,
  removeOption,
  addOptionInput,
  markOptionAsUpdated,
}: VaultPropertyOptionInputsProps) {
  const options = form.values.options.filter(
    option => option.operation !== 'delete',
  );

  return (
    <>
      <Text>Options</Text>

      {options.map(option => (
        <VaultPropertyOptionInput
          key={option.id}
          form={form}
          option={option}
          removeOption={removeOption}
          isSubmitting={isSubmitting}
          markOptionAsUpdated={markOptionAsUpdated}
        />
      ))}

      <Button
        mr="auto"
        variant="subtle"
        onClick={addOptionInput}
        rightIcon={<IconPlus size={16} />}
        disabled={isSubmitting}
      >
        Ajouter une option
      </Button>
    </>
  );
}

type VaultPropertyOptionInputProps = {
  form: UseFormReturnType<FormValues>;
  option: FormVaultPropertyOption;
  removeOption: (id: number) => void;
  isSubmitting: boolean;
  markOptionAsUpdated: (index: number) => void;
};

function VaultPropertyOptionInput({
  form,
  option,
  removeOption,
  isSubmitting,
  markOptionAsUpdated,
}: VaultPropertyOptionInputProps) {
  const index = form.values.options.findIndex(op => op.id === option.id);
  const { t } = useTranslation(['bidResponse']);

  return (
    <Group noWrap key={index} pb="04" spacing="02" align="flex-start">
      <ActionIcon
        onClick={() => removeOption(option.id)}
        disabled={form.values.options.length === 2}
      >
        <IconTrash size={16} />
      </ActionIcon>
      <Stack spacing="01" align="center" w="100%">
        <TextInput
          w="100%"
          variant="filled"
          placeholder={t('vault.sidepanels.createProperty.input.tag.name')}
          {...form.getInputProps(`options.${index}.name`)}
          onChange={e => {
            form.setFieldValue(`options.${index}.name`, e.target.value);
            markOptionAsUpdated(index);
          }}
          disabled={isSubmitting}
        />
        <TextArea
          w="100%"
          variant="filled"
          placeholder={t(
            'vault.sidepanels.createProperty.input.tag.description',
          )}
          {...form.getInputProps(`options.${index}.description`)}
          onChange={e => {
            form.setFieldValue(`options.${index}.description`, e.target.value);
            markOptionAsUpdated(index);
          }}
          disabled={isSubmitting}
        />
      </Stack>
    </Group>
  );
}
